import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWorkspaceObjectives } from '@/lib/workspace-objectives';
import { canEditWorkspaceObjective, type WorkspaceObjective } from '@shared/types/workspace-objectives';

/**
 * `?objective=<id>` (e.g. « Ajouter une mesure » in Aujourd'hui) opens that
 * objective's form when the user may edit it. The parameter stays until the
 * form closes (call the returned function): Profil can mount the panel twice
 * while the user's role loads, and the second panel must open it too.
 */
export const useRequestedObjective = (open: (objective: WorkspaceObjective) => void) => {
  const { objectives, userId, isManager, loading } = useWorkspaceObjectives();
  const [params, setParams] = useSearchParams();
  const requestedId = params.get('objective');
  const clear = useCallback(() => {
    if (!params.has('objective')) return;
    const next = new URLSearchParams(params);
    next.delete('objective');
    setParams(next, { replace: true });
  }, [params, setParams]);
  useEffect(() => {
    if (!requestedId || loading) return;
    const objective = objectives.find((candidate) => candidate.id === requestedId);
    if (objective && canEditWorkspaceObjective(objective, userId, isManager)) open(objective);
    else if (userId) clear();
  }, [requestedId, loading, objectives, userId, isManager, open, clear]);
  return clear;
};
